/**
 * START TYPING ANYWHERE, AND YOU ARE SEARCHING.
 *
 * Theirs (engine.js:682) takes any single printable key pressed on the page, focuses the search box
 * and puts that character in it. No click, no tab, no reaching for a control — the map is a canvas
 * with one text field over it, and typing can only ever have meant one thing.
 *
 * Ours had Escape and nothing else: the search had to be aimed at and clicked before a reader could
 * begin. This is the shortest path in the app made shorter, which is the principle the project
 * already holds itself to.
 *
 * WHAT MUST NOT TRIGGER IT is the whole of the difficulty, and each exclusion is a way it would
 * otherwise break something that already works:
 *
 *   another field      typing in the room title, the rename box or a discussion would be stolen
 *   a shortcut         ctrl-C, cmd-R — the character arrives with a modifier and means something else
 *   a named key        Enter, Tab, ArrowLeft, F5 all have `key.length > 1`, which is theirs' own test
 *   whitespace         a space is how a button is pressed; it must not become a query
 *   contenteditable    the discussion composer is one, and it has no tag name to check
 *
 * Their check omits the last, because their prototype has no rich text anywhere. Ours does.
 */

/** What was pressed, and where — everything the decision needs, and nothing from the DOM. */
export interface Typed {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  /** The tag name of whatever had focus, upper-case as the DOM reports it. */
  tag?: string;
  /** Whether what had focus was editable prose rather than a plain field. */
  editable?: boolean;
}

/**
 * Whether this keystroke should open the search and become its first character.
 *
 * A pure decision so it can be tested against their line without a browser, and so the one place
 * that reads a real event has nothing in it but the reading.
 */
export function typeToSearch({ key, metaKey = false, ctrlKey = false, tag = '', editable = false }: Typed): boolean {
  if (metaKey || ctrlKey) return false;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  if (editable) return false;
  /* Theirs exactly: one character long, and not whitespace. Every named key — Enter, Tab, ArrowUp,
     F5, Escape — is longer than one character, so this one test excludes all of them. */
  return key.length === 1 && /\S/.test(key);
}
